// src/components/partners/SupplierDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { Edit, ArrowLeft, User, Mail, Phone, MapPin, FileText, CreditCard, Badge, Calendar, Clock } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { getSupplierById } from '../../api/partner';

const SupplierDetails = ({ supplierId, onEdit, onBack }) => {
    const { isAdmin, isManager } = useAuth();
    const [supplier, setSupplier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const canModify = isAdmin || isManager;

    useEffect(() => {
        const fetchSupplierDetails = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getSupplierById(supplierId);
                setSupplier(data);
            } catch (err) {
                setError('Impossible de charger les détails du fournisseur. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (supplierId) {
            fetchSupplierDetails();
        }
    }, [supplierId]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (loading) {
        return (
            <Card className="shadow-sm">
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement des détails du client...</p>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="danger">{error}</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (!supplier) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="warning">Aucun client trouvé.</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center">
                    <User size={20} className="me-2 text-primary" />
                    Détails du client
                </h5>
                {canModify && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onEdit(supplierId)}
                    >
                        <Edit size={16} className="me-1" />
                        Modifier
                    </Button>
                )}
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="mb-4">
                            <h4>{supplier.name}</h4>
                            {supplier.contactPerson && (
                                <p className="text-muted mb-2 d-flex align-items-center">
                                    <User size={16} className="me-2" />
                                </p>
                            )}
                            {supplier.email && (
                                <p className="text-muted mb-2 d-flex align-items-center">
                                    <Mail size={16} className="me-2" />
                                    {supplier.email}
                                </p>
                            )}
                            {supplier.phone && (
                                <p className="text-muted mb-2 d-flex align-items-center">
                                    <Phone size={16} className="me-2" />
                                    {supplier.phone}
                                </p>
                            )}
                        </div>

                        {supplier.address && (
                            <div className="mb-4">
                                <h6 className="mb-2 d-flex align-items-center">
                                    <MapPin size={16} className="me-2" />
                                    Adresse
                                </h6>
                                <div className="bg-light p-3 rounded">
                                    {supplier.address.split('\n').map((line, index) => (
                                        <p key={index} className={index !== 0 ? 'mt-1 mb-0' : 'mb-0'}>
                                            {line}
                                        </p>
                                    ))}
                                </div>
                               
                            </div>
                            
                            
                        )}
                    </Col>

                    <Col md={6}>
                        <Table bordered size="sm" className="mt-md-0 mt-3">
                            <tbody>
                                {supplier.taxIdentification && (
                                    <tr>
                                        <td className="bg-light" width="40%">
                                            <FileText size={14} className="me-1" /> Identifiant fiscal
                                        </td>
                                        <td>
                                            {supplier.taxIdentification}
                                        </td>
                                    </tr>
                                )}
                                {supplier.bankDetails && (
                                    <tr>
                                        <td className="bg-light">
                                            <CreditCard size={14} className="me-1" /> Coordonnées bancaires
                                        </td>
                                        <td>{supplier.bankDetails}</td>
                                    </tr>
                                )}
                                {supplier.paymentTerms && (
                                    <tr>
                                        <td className="bg-light">
                                            {/* <DollarSign size={14} className="me-1" /> Conditions de paiement */}
                                        </td>
                                        <td>{supplier.paymentTerms}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="bg-light">
                                        <Calendar size={14} className="me-1" /> Créé le
                                    </td>
                                    <td>{formatDate(supplier.createdAt)}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light">
                                        <Clock size={14} className="me-1" /> Dernière mise à jour
                                    </td>
                                    <td>{formatDate(supplier.updatedAt)}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>

                <div className="d-flex justify-content-start mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        <ArrowLeft size={16} className="me-1" />
                        Retour à la liste
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default SupplierDetails;